// Liest den PX-Connect-Schadenbericht (Draft in Projekt.metadata, ADR 0006) für die
// Kalkulationsseiten-Tabelle und kapselt das Löschen. Genau ein Bericht pro Projekt.
import { useMutation, useQuery } from '@apollo/client';
import { GetProjectDocument, UpdateProjectMetadataDocument, type Projekt } from '~/gql/ucpw/graphql';
import {
    assertProjectMetadataSaved,
    letzterPxBearbeitungsstand,
    pxConnectDraftFromMetadata,
    removePxConnectSchadenberichtMetadata,
    type PxConnectSchadenberichtDraft,
} from '~/meta/data/px-connect';

type Args = {
    projectId?: number;
    /** Wenn gesetzt, muss der Draft zur serviceRequestId des Subprojekts passen. */
    serviceRequestId?: string;
};

export const usePxConnectSchadenbericht = ({ projectId, serviceRequestId }: Args) => {
    const context = { clientName: 'ucpw' };
    // Kein network-only: der Draft wird über Cache-Writes von persistDraft/markSent/Löschen
    // (dieselbe GetProject-Cache-Entry, normalisiert nach Projekt:id) aktuell gehalten.
    const { data, loading } = useQuery(GetProjectDocument, {
        variables: { id: projectId as number },
        context,
        skip: !projectId,
    });
    const project = data?.item?.item as Projekt | undefined;

    const draft = pxConnectDraftFromMetadata(project?.metadata);
    const exists =
        Boolean(draft?.answers?.length) &&
        (!serviceRequestId || draft?.serviceRequestId === serviceRequestId);

    const [updateProjectMetadata, { loading: deleting }] = useMutation(
        UpdateProjectMetadataDocument,
        { context }
    );

    const remove = async () => {
        if (!project) return;
        const result = await updateProjectMetadata({
            variables: {
                projektId: project.id,
                version: project.version,
                forceOverwrite: true,
                metadata: removePxConnectSchadenberichtMetadata(project.metadata),
            },
        });
        assertProjectMetadataSaved(result);
    };

    return {
        draft: (exists ? draft : undefined) as PxConnectSchadenberichtDraft | undefined,
        exists,
        /** Stand des zuletzt versendeten Berichts (UCPWO-410), sonst `undefined`. */
        letzterStand: letzterPxBearbeitungsstand(project?.metadata),
        loading,
        deleting,
        remove,
    };
};
